<template>
  <div class="chart-container" :style="{ height: height }">
    <!-- 动态图例 -->
    <div class="chart-legend" v-if="!loading && !error && allStages.length">
      <span class="legend-item" v-for="(stage, idx) in allStages" :key="stage">
        <span class="legend-color" :style="{ backgroundColor: stageColor(idx) }"></span>
        {{ stage }}舞台
      </span>
      <span class="legend-item">
        <span class="legend-color" style="background-color: #ff4d4f;"></span> 总人数
      </span>
    </div>
    <div v-if="loading" class="chart-loading">
      <i class="fas fa-spinner fa-spin"></i>
    </div>
    <div v-else-if="error" class="chart-error">
      <i class="fas fa-exclamation-circle"></i> {{ error }}
    </div>
    <v-chart v-else class="chart" :option="chartOption" autoresize @click="handleChartClick" />
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import { toRaw } from 'vue'
import { getStageColorByIndex } from '../utils/stageColors'

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
])

export default {
  name: 'StagePeakLineChart',
  components: { VChart },
  props: {
    activeDate: {
      type: String,
      required: true,
    },
    height: {
      type: String,
      default: '400px',
    },
    stageData: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      localStageData: []
    }
  },
  created() {
    const stageData = sessionStorage.getItem('stageData')
    this.localStageData = stageData ? JSON.parse(stageData) : []
  },
  computed: {
    // 动态获取所有舞台名称（有序、去重）
    allStages() {
      const stages = [...new Set(
        toRaw(this.localStageData).map(item => item.stage).filter(Boolean)
      )].sort()
      return stages
    },

    chartOption() {
      const rawData = toRaw(this.localStageData)
      if (!rawData || rawData.length === 0) {
        return {
          title: { text: '无数据' },
          xAxis: { type: 'category', data: [] },
          yAxis: { type: 'value' },
          series: [],
        }
      }

      // 整合数据：按舞台 → 时间点 → 累加观众数
      const stageMap = {}
      const timeSet = new Set()

      rawData.forEach(({ stage, rounded_datetime, stage_count }) => {
        if (!stageMap[stage]) stageMap[stage] = {}
        timeSet.add(rounded_datetime)
        stageMap[stage][rounded_datetime] = (stageMap[stage][rounded_datetime] || 0) + stage_count
      })

      const allTimes = Array.from(timeSet).sort()
      const stages = Object.keys(stageMap).sort()

      const series = stages.map((stage, idx) => ({
        name: `${stage}舞台`,
        type: 'line',
        data: allTimes.map(t => stageMap[stage][t] ?? null),
        smooth: true,
        connectNulls: true,
        itemStyle: {
          color: getStageColorByIndex(idx),
        },
      }))

      // 总人数系列
      const totalSeries = {
        name: '总人数',
        type: 'line',
        data: allTimes.map(t =>
          Object.values(stageMap).reduce((sum, dataMap) => sum + (dataMap[t] ?? 0), 0)
        ),
        smooth: true,
        connectNulls: true,
        itemStyle: { color: '#ff4d4f' },
        silent: true,
      }
      series.push(totalSeries)

      return {
        title: {
          text: `${this.formatDate(this.activeDate)} 舞台总观众趋势`,
          left: 'center',
          top: 10,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' },
          formatter: function (params) {
            let tooltipText = params[0].name + '<br/>'
            params.forEach(param => {
              tooltipText += param.marker + param.seriesName + ': ' + param.value + '<br/>'
              if (param.seriesName !== '总人数') {
                const stageName = param.seriesName.replace('舞台', '')
                const artistInfo = rawData.find(item =>
                  item.rounded_datetime === param.name && item.stage === stageName
                )
                if (artistInfo) {
                  tooltipText += '乐队: ' + (artistInfo.name || '空闲中') + '<br/>'
                }
              }
            })
            return tooltipText
          }
        },
        legend: {
          top: 40,
          data: series.map(s => s.name),
        },
        grid: {
          top: 80,
          left: 30,
          right: 30,
          bottom: 120,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: allTimes,
          axisLabel: {
            rotate: 45,
            formatter: val => val.slice(11, 19)
          },
        },
        yAxis: {
          type: 'value',
          name: '观众人数',
        },
        series,
      }
    },
  },
  methods: {
    stageColor(idx) {
      return getStageColorByIndex(idx)
    },
    formatDate(dateStr) {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}月${date.getDate()}日`
    },
    handleChartClick(params) {
      const { name, seriesName } = params
      if (name && seriesName) {
        const stageName = seriesName.replace('舞台', '')
        const artistInfo = toRaw(this.localStageData).find(item =>
          item.rounded_datetime === name && item.stage === stageName
        )
        if (artistInfo) {
          const artistDetails = {
            artist: artistInfo.name,
            date: name.slice(0, 10)
          }
          this.$emit('artistClick', artistDetails)
        }
      }
    }
  },
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.chart {
  width: 100%;
  height: 100%;
}

.chart-loading,
.chart-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: rgba(0, 0, 0, 0.2);
}

.chart-legend {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 13px;
  color: #666;
  padding-left: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-color {
  width: 12px;
  height: 12px;
  display: inline-block;
  border-radius: 2px;
  border: 1px solid #ccc;
}
</style>